// File: src/components/MyRCMImport.js
// MyRCM import with fuzzy name matching and manual driver mapping

import React, { useState } from 'react';
import { X, FileSpreadsheet, User, Compass, Link2 } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { calculatePointsForPosition, calculateQualificationPoints, parseMyRCMRankingRows } from '../utils/utils';
import { fuzzyMatchDriver, matchNeedsReview } from '../utils/driverMatching';
import { useLanguage } from '../contexts/LanguageContext';
import { handleUnauthorized } from '../utils/session';
import MyRCMEventPicker from './MyRCMEventPicker';

function MyRCMImport({
  showMyRCMImportModal,
  setShowMyRCMImportModal,
  myrcmImportRaceId,
  seriesClass,
  token,
  loadSeries
}) {
  const { t } = useLanguage();

  const [myrcmBaseUrl, setMyrcmBaseUrl] = useState('');
  const [myrcmPreview, setMyrcmPreview] = useState(null);
  const [myrcmLoading, setMyrcmLoading] = useState(false);
  const [myrcmMessage, setMyrcmMessage] = useState('');
  const [showDriverMapping, setShowDriverMapping] = useState(false);
  const [importQualAsFinal, setImportQualAsFinal] = useState(false);
  // 'browse' = pick the event and class from MyRCM's own list in-app;
  // 'paste' = the original hand-copied URL, kept as a fallback.
  const [sourceMode, setSourceMode] = useState('browse');
  const [myrcmSelection, setMyrcmSelection] = useState(null);
  // Classifying the message explicitly - the old check read the message text
  // for '✓' / 'Error', which never matched once t('error') was translated.
  const [myrcmMessageType, setMyrcmMessageType] = useState('info');


// Helper to ensure reportKey is in URL
const ensureReportKey = (url, defaultKey) => {
  if (!url) return '';

  // If URL already has reportKey, return as-is
  if (url.includes('?reportKey=') || url.includes('&reportKey=')) {
    return url;
  }

  // Add the default reportKey
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}reportKey=${defaultKey}`;
};

  const fetchMyRCMResults = async () => {
    if (!myrcmBaseUrl) {
      setMyrcmMessage(t('pleaseEnterFinalResultsUrl'));
      return;
    }

    // Clean URL by removing hash fragments and query parameters
    const baseUrl = myrcmBaseUrl.split('#')[0].split('?')[0].trim();

    // Build URLs with appropriate reportKeys
    // If importing qual as final, use qual reportKey (202), otherwise use final reportKey (203)
    const finalUrlWithKey = ensureReportKey(baseUrl, importQualAsFinal ? '202' : '203');

    // Auto-derive qualification URL from base URL if qualification points are enabled
    let qualUrlWithKey = '';
    if (seriesClass.calculate_qualification_points && !importQualAsFinal) {
      qualUrlWithKey = ensureReportKey(baseUrl, '202');
    }

    setMyrcmLoading(true);
    setMyrcmMessage('');
    setMyrcmPreview(null);

    try {
      // Use our backend proxy instead of allorigins
      const proxyUrl = `${API_BASE_URL}/proxy/myrcm?url=${encodeURIComponent(finalUrlWithKey)}`;
      const response = await fetch(proxyUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        throw new Error(t('failedToFetchMyRCM'));
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // MyRCM v9 renders finals and qualifying with the same table layout,
      // so a single parser handles both (including the importQualAsFinal case).
      const parsedRows = parseMyRCMRankingRows(doc);

      if (parsedRows === null) {
        throw new Error(t('couldNotFindResultsTable'));
      }

      const results = [];
      const unmatchedDrivers = [];

      console.log(`Found ${parsedRows.length} driver rows in table`);

      for (const { rank, carNumber, driverName } of parsedRows) {
        console.log(`Parsing row: rank=${rank}, car=${carNumber}, driver="${driverName}"`);

        // Try fuzzy matching
        const fuzzyMatch = fuzzyMatchDriver(driverName, carNumber, seriesClass.drivers || []);
        const existingDriver = fuzzyMatch ? fuzzyMatch.driver : null;
        const points = calculatePointsForPosition(rank);

        if (existingDriver) {
          results.push({
            driver_id: existingDriver.id,
            driver_name: existingDriver.name,
            car_number: carNumber,
            imported_name: driverName,
            position: rank,
            qualification_position: null,
            points,
            qualification_points: 0,
            isNew: false,
            isMatched: true,
            // Nothing about the name agreed - worth a human look before import.
            needsReview: matchNeedsReview(fuzzyMatch)
          });
        } else {
          // No automatic match - add to unmatched list
          unmatchedDrivers.push(driverName);
          results.push({
            driver_name: driverName,
            car_number: carNumber,
            imported_name: driverName,
            position: rank,
            qualification_position: null,
            points,
            qualification_points: 0,
            isNew: true,
            isMatched: false,
            manualMatch: null
          });
        }
      }

// Fetch qualification results if URL provided
if (qualUrlWithKey && seriesClass.calculate_qualification_points) {
  console.log('Fetching qualification from:', qualUrlWithKey);

  try {
    const qualProxyUrl = `${API_BASE_URL}/proxy/myrcm?url=${encodeURIComponent(qualUrlWithKey)}`;
    const qualResponse = await fetch(qualProxyUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    console.log('Qual response status:', qualResponse.ok, qualResponse.status);
    
    if (qualResponse.ok) {
      const qualHtml = await qualResponse.text();
      console.log('Qual HTML length:', qualHtml.length);

      const qualDoc = parser.parseFromString(qualHtml, 'text/html');
      const qualParsedRows = parseMyRCMRankingRows(qualDoc);
      console.log('Qual rows found:', qualParsedRows ? qualParsedRows.length : 'no table');

      if (qualParsedRows) {
        let processedCount = 0;
        let matchedCount = 0;

        for (const { rank: qualRank, driverName: qualPilot } of qualParsedRows) {
          processedCount++;

          // Only top 5 get qualification points
          if (qualRank <= 5) {
            const qualPoints = calculateQualificationPoints(qualRank);
            console.log(`Top 5: ${qualPilot} - Q${qualRank} = ${qualPoints}pts`);

            const result = results.find(r =>
              r.imported_name.toLowerCase() === qualPilot.toLowerCase()
            );

            if (result) {
              result.qualification_position = qualRank;
              result.qualification_points = qualPoints;
              matchedCount++;
              console.log(`✓ Matched: ${qualPilot} -> ${result.driver_name || result.imported_name}`);
            } else {
              console.log(`✗ No match for: ${qualPilot}`);
            }
          }
        }

        console.log(`Qual complete: ${processedCount} processed, ${matchedCount} matched`);
      } else {
        console.log('ERROR: No ranking table in qual page');
      }
    } else {
      console.log('ERROR: Qual fetch failed:', qualResponse.status);
    }
  } catch (qualError) {
    console.log('ERROR fetching qual:', qualError);
  }
}

      console.log(`Parsing complete: ${results.length} drivers found`);

      if (results.length === 0) {
        console.error('ERROR: No drivers found after parsing all rows');
        console.error('This could mean:');
        console.error('1. The table structure is different than expected');
        console.error('2. All rows were filtered out due to validation rules');
        console.error('3. The pilot name is in a different cell than cells[3]');
        throw new Error(t('noDriversFoundInTable'));
      }

      setMyrcmPreview({
        results,
        newDrivers: unmatchedDrivers,
        hasUnmatched: unmatchedDrivers.length > 0
      });

      let msg = t('successfullyFetchedFromMyRCM').replace('{count}', results.length);
      if (unmatchedDrivers.length > 0) {
        msg += `\n` + t('driversNeedManualMatching').replace('{count}', unmatchedDrivers.length);
      }
      setMyrcmMessage(msg);
      setMyrcmMessageType('success');

    } catch (error) {
      setMyrcmMessage(t('error') + ': ' + error.message);
      setMyrcmMessageType('error');
      console.error('MyRCM import error:', error);
    } finally {
      setMyrcmLoading(false);
    }
  };

  const updateDriverMapping = (importedName, selectedDriverId) => {
    setMyrcmPreview(prev => ({
      ...prev,
      results: prev.results.map(r => {
        if (r.imported_name === importedName) {
          if (selectedDriverId === 'new') {
            return { ...r, isNew: true, isMatched: false, manualMatch: null, driver_id: null };
          } else if (selectedDriverId) {
            const driver = seriesClass.drivers.find(d => d.id === parseInt(selectedDriverId));
            return {
              ...r,
              driver_id: parseInt(selectedDriverId),
              driver_name: driver.name,
              isNew: false,
              isMatched: true,
              manualMatch: driver.name
            };
          }
        }
        return r;
      }),
      newDrivers: prev.results
        .filter(r => r.imported_name === importedName && selectedDriverId === 'new')
        .map(r => r.imported_name)
        .concat(prev.newDrivers.filter(n => n !== importedName))
    }));
  };

  const importMyRCMResults = async () => {
    if (!myrcmPreview || myrcmPreview.results.length === 0) return;

    setMyrcmLoading(true);

    try {
      // Create new drivers
      const newDriverIds = {};
      const driversToCreate = myrcmPreview.results.filter(r => r.isNew && !r.driver_id);
      
      for (const result of driversToCreate) {
        const response = await fetch(`${API_BASE_URL}/drivers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: result.imported_name,
            car_number: result.car_number,
            series_class_id: seriesClass.id
          })
        });

        if (handleUnauthorized(response)) return;

        if (response.ok) {
          const newDriver = await response.json();
          newDriverIds[result.imported_name] = newDriver.id;
        }
      }

      // Prepare rankings
      const rankings = myrcmPreview.results.map(result => {
        let driverId;
        if (result.driver_id) {
          driverId = result.driver_id;
        } else {
          driverId = newDriverIds[result.imported_name];
        }
        
        return {
          driverId: driverId,
          position: result.position,
          qualificationPosition: result.qualification_position,
          points: result.points,
          qualificationPoints: result.qualification_points
        };
      });

      // Import all rankings
      const rankingsResponse = await fetch(`${API_BASE_URL}/races/${myrcmImportRaceId}/rankings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rankings })
      });

      if (handleUnauthorized(rankingsResponse)) return;
      if (!rankingsResponse.ok) {
        const data = await rankingsResponse.json().catch(() => ({}));
        setMyrcmMessage('Import failed: ' + (data.error || rankingsResponse.status));
        setMyrcmMessageType('error');
        return;
      }

      const createdCount = driversToCreate.length;
      setMyrcmMessageType('success');
      setMyrcmMessage(
        t('successfullyFetchedFromMyRCM').replace('{count}', myrcmPreview.results.length) +
        (createdCount > 0
          ? `\n\n${createdCount} ${t('newDriversWillBeCreated')}\n${driversToCreate.map(d => d.imported_name).join(', ')}\n\n${t('tipAddClubAndBirthYear')}`
          : '')
      );

      await loadSeries();

      setTimeout(() => {
        setShowMyRCMImportModal(false);
        resetImportState();
      }, 3000);

    } catch (error) {
      setMyrcmMessage('Import failed: ' + error.message);
      setMyrcmMessageType('error');
    } finally {
      setMyrcmLoading(false);
    }
  };

  // Both the close button and the post-import auto-close reset the dialog; keep
  // that in one place so the two can't drift apart as state is added.
  const resetImportState = () => {
    setMyrcmPreview(null);
    setMyrcmBaseUrl('');
    setMyrcmMessage('');
    setMyrcmMessageType('info');
    setShowDriverMapping(false);
    setImportQualAsFinal(false);
    setSourceMode('browse');
    setMyrcmSelection(null);
  };

  const closeModal = () => {
    setShowMyRCMImportModal(false);
    resetImportState();
  };

  // Picking a class in the browser produces exactly the URL that used to be
  // pasted here by hand, so everything downstream is unchanged.
  const handlePickerSelect = (baseUrl, selection) => {
    setMyrcmBaseUrl(baseUrl);
    setMyrcmSelection(selection);
    setMyrcmPreview(null);
    setMyrcmMessage('');
    setMyrcmMessageType('info');
  };

  if (!showMyRCMImportModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-paper rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-line">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-ink">{t('importFromMyRCM')}</h3>
          <button onClick={closeModal} className="text-ink-muted hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-3 rounded">
            <p className="font-semibold mb-2">{t('howToImportFromMyRCM')}</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              {sourceMode === 'browse' ? (
                <>
                  <li>{t('myrcmBrowseStep1')}</li>
                  <li>{t('myrcmBrowseStep2')}</li>
                  <li>{t('myrcmBrowseStep3')}</li>
                </>
              ) : (
                <>
                  <li>{t('myrcmStep1')}</li>
                  <li>{t('myrcmStep2')}</li>
                  <li>{t('myrcmStep3')}</li>
                  <li>{t('myrcmStep4')}</li>
                  <li>{t('myrcmStep5')}</li>
                </>
              )}
              <li>{t('myrcmStep6')}</li>
              <li>{t('myrcmStep7')}</li>
            </ol>
          </div>

          {/* Source: browse MyRCM's event list, or paste a URL as before */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSourceMode('browse')}
              className={`flex items-center gap-2 px-4 py-2 rounded font-semibold ${
                sourceMode === 'browse' ? 'bg-accent text-accent-ink' : 'bg-paper-alt text-ink-muted border border-line'
              }`}
            >
              <Compass className="w-4 h-4" />
              {t('browseMyrcmEvents')}
            </button>
            <button
              type="button"
              onClick={() => setSourceMode('paste')}
              className={`flex items-center gap-2 px-4 py-2 rounded font-semibold ${
                sourceMode === 'paste' ? 'bg-accent text-accent-ink' : 'bg-paper-alt text-ink-muted border border-line'
              }`}
            >
              <Link2 className="w-4 h-4" />
              {t('pasteUrlInstead')}
            </button>
          </div>

          {sourceMode === 'browse' && (
            myrcmSelection ? (
              <div className="flex items-center justify-between gap-3 bg-paper-alt border border-line rounded px-4 py-3">
                <span className="text-ink truncate">
                  <span className="text-ink-muted">{t('myrcmSelectedEvent')}: </span>
                  {myrcmSelection.eventName} — <strong>{myrcmSelection.className}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => { setMyrcmSelection(null); setMyrcmBaseUrl(''); }}
                  disabled={myrcmLoading}
                  className="text-ink-muted underline hover:text-ink shrink-0"
                >
                  {t('myrcmChangeSelection')}
                </button>
              </div>
            ) : (
              <MyRCMEventPicker
                token={token}
                onSelect={handlePickerSelect}
                onRequestPasteMode={() => setSourceMode('paste')}
                disabled={myrcmLoading}
              />
            )
          )}

          {sourceMode === 'paste' && (
            <div>
              <label className="block text-ink-muted mb-2 font-semibold">
                {t('myrcmResultsUrlLabel')} <span className="text-red-700 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={myrcmBaseUrl}
                onChange={(e) => { setMyrcmBaseUrl(e.target.value); setMyrcmSelection(null); }}
                placeholder="https://www.myrcm.ch/en/report/98862/393406"
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                disabled={myrcmLoading}
              />
              <p className="text-sm text-ink-muted mt-1">
                {seriesClass.calculate_qualification_points && !importQualAsFinal
                  ? t('urlWillBeUsedForBothFinalAndQual')
                  : t('enterTheBaseUrlWithoutReportKey')}
              </p>
            </div>
          )}

          {/* Import Qualification as Final Option */}
          <div className="flex items-center gap-3 bg-paper-alt border border-line rounded p-4">
            <input
              type="checkbox"
              id="importQualAsFinal"
              checked={importQualAsFinal}
              onChange={(e) => setImportQualAsFinal(e.target.checked)}
              className="w-5 h-5 rounded"
              disabled={myrcmLoading}
            />
            <label htmlFor="importQualAsFinal" className="text-ink-muted cursor-pointer">
              {t('importQualificationAsFinal')}
              <span className="block text-sm text-ink-muted mt-1">{t('importQualificationAsFinalDescription')}</span>
            </label>
          </div>

          {/* Fetch Button */}
          <button
            onClick={fetchMyRCMResults}
            disabled={myrcmLoading || !myrcmBaseUrl}
            className="w-full bg-accent hover:opacity-90 disabled:bg-paper-alt disabled:text-ink-muted text-accent-ink py-3 rounded font-semibold flex items-center justify-center gap-2"
          >
            {myrcmLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('fetchingFromMyRCM')}
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-5 h-5" />
                {t('fetchAndPreview')}
              </>
            )}
          </button>

          {/* Messages */}
          {myrcmMessage && (
            <div className={`px-4 py-3 rounded ${
              myrcmMessageType === 'success'
                ? 'bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200'
                : myrcmMessageType === 'error'
                ? 'bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200'
                : 'bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200'
            }`}>
              <pre className="whitespace-pre-wrap font-sans">{myrcmMessage}</pre>
            </div>
          )}

          {/* Preview */}
          {myrcmPreview && (
            <div className="border border-line rounded p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold text-ink">
                  {t('preview')}
                  {/* MyRCM car numbers are per class, so spell out which class
                      these results came from before they are imported. */}
                  {myrcmSelection && (
                    <span className="block text-sm font-normal text-ink-muted">
                      {myrcmSelection.eventName} — {myrcmSelection.className}
                    </span>
                  )}
                </h4>
                {myrcmPreview.hasUnmatched && (
                  <button
                    onClick={() => setShowDriverMapping(!showDriverMapping)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    {showDriverMapping ? t('hideDriverMapping') : t('showDriverMapping')}
                  </button>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-2 rounded mb-4">
                <strong>✓ {myrcmPreview.results.length} {t('resultsReadyToImport')}</strong>
                {myrcmPreview.newDrivers.length > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs">{t('unmatched')}</span>
                    {' '}{myrcmPreview.newDrivers.length} {t('newDriversWillBeCreated')}
                  </div>
                )}
              </div>

              {/* Driver Mapping Section */}
              {showDriverMapping && myrcmPreview.hasUnmatched && (
                <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 rounded p-4 mb-4">
                  <h5 className="text-ink font-bold mb-3">{t('matchDrivers')}</h5>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm mb-3">
                    {t('someDriversNotMatched')}
                  </p>
                  {myrcmPreview.results.filter(r => !r.isMatched).map((result, idx) => (
                    <div key={idx} className="bg-paper-alt rounded p-3 mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-ink font-medium flex-1">{result.imported_name}</span>
                        <select
                          value={result.manualMatch || (result.isNew ? 'new' : '')}
                          onChange={(e) => updateDriverMapping(result.imported_name, e.target.value)}
                          className="px-3 py-1 bg-paper-alt border border-line rounded text-ink text-sm"
                        >
                          <option value="new">{t('createNewDriver').replace('{carNumber}', result.car_number)}</option>
                          <optgroup label={t('matchToExistingDriver')}>
                            {seriesClass.drivers?.map(driver => (
                              <option key={driver.id} value={driver.id}>
                                {driver.car_number ? `#${driver.car_number} - ` : ''}{driver.name}
                                {driver.club ? ` (${driver.club})` : ''}
                                {driver.birth_year ? `, b.${driver.birth_year}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Results Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left text-ink-muted pb-2 pr-4">{t('driver')}</th>
                      <th className="text-center text-ink-muted pb-2 px-2">{t('pos')}</th>
                      <th className="text-center text-ink-muted pb-2 px-2">{t('pts')}</th>
                      {seriesClass.calculate_qualification_points && (
                        <>
                          <th className="text-center text-ink-muted pb-2 px-2">{t('qPos')}</th>
                          <th className="text-center text-ink-muted pb-2 px-2">{t('qPts')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {myrcmPreview.results.map((result, idx) => (
                      <tr key={idx} className="border-b border-line">
                        <td className="py-2 pr-4">
                          <div>
                            <span className="text-ink">{result.driver_name || result.imported_name}</span>
                            {result.isNew && (
                              <span className="ml-2 bg-orange-600 text-white px-2 py-0.5 rounded text-xs">{t('new')}</span>
                            )}
                            {result.manualMatch && (
                              <span className="ml-2 bg-green-600 text-white px-2 py-0.5 rounded text-xs">{t('matched')}</span>
                            )}
                            {result.needsReview && (
                              <span
                                className="ml-2 bg-yellow-600 text-white px-2 py-0.5 rounded text-xs"
                                title={t('matchedByCarNumberOnlyHint')}
                              >
                                {t('checkMatch')}
                              </span>
                            )}
                          </div>
                          {result.imported_name !== result.driver_name && result.driver_name && (
                            <div className="text-xs text-ink-muted">{t('myrcmLabel')} {result.imported_name}</div>
                          )}
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="text-ink font-semibold">P{result.position}</span>
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="text-yellow-700 dark:text-yellow-400 font-mono-num">{result.points}</span>
                        </td>
                        {seriesClass.calculate_qualification_points && (
                          <>
                            <td className="text-center py-2 px-2">
                              {result.qualification_position ? (
                                <span className="text-green-700 dark:text-green-400">Q{result.qualification_position}</span>
                              ) : (
                                <span className="text-ink-muted">-</span>
                              )}
                            </td>
                            <td className="text-center py-2 px-2">
                              {result.qualification_points > 0 ? (
                                <span className="text-green-700 dark:text-green-400 font-mono-num">{result.qualification_points}</span>
                              ) : (
                                <span className="text-ink-muted">-</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={importMyRCMResults}
                disabled={myrcmLoading}
                className="mt-4 w-full bg-green-600 hover:bg-green-700 disabled:bg-paper-alt disabled:text-ink-muted text-white py-3 rounded font-semibold"
              >
                {myrcmLoading ? t('importing') : t('importResults').replace('{count}', myrcmPreview.results.length)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyRCMImport;